/**
 * FicheSection.tsx
 * ─────────────────
 * Section "La fiche SmartLawyer — Tout ce qu'il faut savoir sans lire les 30 pages."
 * Réutilisable sur toutes les pages publiques.
 *
 * Usage :
 *   <FicheSection onSearch={() => {}} />
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface FicheSectionProps {
  /** Callback pour lancer une recherche depuis le CTA */
  onSearch?: () => void;
}

// ── 4 points clés de la fiche (clés i18n, traduites au rendu) ─────────────────
const FICHE_POINTS = [
  {
    n:     '1',
    titleKey: 'fiche.section.p1_title',
    descKey:  'fiche.section.p1_desc',
  },
  {
    n:     '2',
    titleKey: 'fiche.section.p2_title',
    descKey:  'fiche.section.p2_desc',
  },
  {
    n:     '3',
    titleKey: 'fiche.section.p3_title',
    descKey:  'fiche.section.p3_desc',
  },
  {
    n:     '4',
    titleKey: 'fiche.section.p4_title',
    descKey:  'fiche.section.p4_desc',
  },
];

// ── Mockup fiche d'arrêt ──────────────────────────────────────────────────────
const FicheMock = () => {
  const { t } = useTranslation();
  return (
  <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-[0_8px_28px_rgba(0,0,0,0.06)]">

    {/* Header fiche */}
    <div className="bg-card px-5 py-4 border-b border-border">
      {/* Badge solution */}
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400 mb-2">
        {t('fiche.section.mock_solution')}
      </span>
      {/* Juridiction */}
      <p className="font-serif text-[15px] font-bold text-foreground mb-0.5">
        Cour de cassation
      </p>
      <p className="text-[11px] text-muted-foreground mb-2.5">
        {t('fiche.section.mock_meta')}
      </p>
      {/* Tags domaine */}
      <div className="flex gap-1.5 flex-wrap">
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-medium">
          {t('fiche.section.mock_tag_tax')}
        </span>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-medium">
          {t('fiche.section.mock_tag_liability')}
        </span>
        <span className="text-[10px] px-2 py-0.5 rounded-full border border-border text-muted-foreground">
          F-D
        </span>
      </div>
    </div>

    {/* Corps fiche */}
    <div className="px-5 py-4 space-y-3">

      {/* Kicker */}
      <p className="text-[9px] font-bold uppercase tracking-[.12em] text-primary">
        {t('fiche.section.mock_kicker')}
      </p>

      {/* Problème de droit — fond marine */}
      <div className="rounded-[11px_11px_0_0] overflow-hidden">
        <div className="px-4 py-3" style={{ background: '#1c2333' }}>
          <div className="flex items-center gap-2 mb-1.5">
            <p className="text-[8px] font-bold uppercase tracking-[.1em] text-white/70">
              {t('fiche.problem')}
            </p>
            <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400 border border-violet-200/50">
              {t('fiche.section.exclusive')}
            </span>
          </div>
          <p className="font-serif text-[11px] italic font-light text-white leading-relaxed">
            {t('fiche.section.mock_problem')}
          </p>
        </div>

        {/* Attendu */}
        <div className="border border-t-0 border-border rounded-[0_0_11px_11px] px-4 py-3 mb-3">
          <p className="text-[8px] font-bold uppercase tracking-[.1em] text-primary mb-1.5">
            {t('fiche.section.mock_attendu_label')}
          </p>
          <p className="font-serif text-[11px] italic text-foreground border-l-2 border-primary/30 pl-2 leading-relaxed">
            {t('fiche.section.mock_attendu')}
          </p>
        </div>
      </div>

      {/* Articles visés */}
      <div className="flex gap-1.5 flex-wrap">
        <span className="inline-flex items-center gap-1.5 px-2 py-1 border-[1.5px] border-primary rounded-lg text-[10px] font-medium text-foreground">
          <span className="text-[8px] font-bold text-muted-foreground uppercase pr-1.5 border-r border-border mr-0.5">LPF</span>
          art. L. 267 ★
        </span>
        <span className="inline-flex items-center gap-1.5 px-2 py-1 border-[1.5px] border-primary rounded-lg text-[10px] font-medium text-foreground">
          <span className="text-[8px] font-bold text-muted-foreground uppercase pr-1.5 border-r border-border mr-0.5">CGI</span>
          art. 297 A
        </span>
      </div>
    </div>
  </div>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────
export const FicheSection = ({ onSearch }: FicheSectionProps) => {
  const { t } = useTranslation();
  return (
  <section className="py-20 bg-muted/30 border-t border-border">
    <div className="max-w-[1100px] mx-auto px-6 sm:px-8">

      {/* ── Grille texte + mockup ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-14 items-end">

        {/* Colonne gauche — texte */}
        <div>
          <span className="block text-[10px] font-bold uppercase tracking-[.14em] text-primary mb-2.5">
            {t('fiche.section.kicker')}
          </span>
          <h2 className="font-serif text-[30px] sm:text-[34px] font-bold text-foreground tracking-[-0.03em] leading-tight mb-8">
            {t('fiche.section.title_1')}<br />
            <em className="not-italic text-primary">{t('fiche.section.title_2')}</em>
          </h2>

          {/* 4 points */}
          <div className="flex flex-col gap-5">
            {FICHE_POINTS.map(({ n, titleKey, descKey }) => (
              <div key={n} className="flex items-start gap-3.5">
                {/* Numéro */}
                <div className="w-7 h-7 rounded-full bg-primary/10 text-primary text-[12px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {n}
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-foreground mb-0.5">{t(titleKey)}</p>
                  <p className="text-[12px] text-muted-foreground leading-relaxed">{t(descKey)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Bandeau CTA */}
          <div className="mt-7 px-5 py-4 bg-primary/5 border border-primary/20 rounded-2xl flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[13px] text-foreground font-medium mb-0.5">
                {t('fiche.section.cta_title')}
              </p>
              <p className="text-[12px] text-primary font-semibold">
                {t('fiche.section.cta_quota')}
              </p>
            </div>
            {onSearch ? (
              <button
                onClick={onSearch}
                className="flex-shrink-0 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-[13px] font-semibold hover:bg-primary/90 transition-colors"
              >
                {t('fiche.section.cta_button')}
              </button>
            ) : (
              <Link
                to="/"
                className="flex-shrink-0 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-[13px] font-semibold hover:bg-primary/90 transition-colors"
              >
                {t('fiche.section.cta_button')}
              </Link>
            )}
          </div>
        </div>

        {/* Colonne droite — mockup */}
        <FicheMock />
      </div>

    </div>
  </section>
  );
};